/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {FormattedMessage, useIntl} from 'react-intl'
import {validatePassword} from '../../utils/password-utils'
import {
    VALIDATE_EMAIL_PATTERN,
    GENERAL_FORM_VALIDATION_MESSAGES as generalMessages,
    HYBRID_CONTENT
} from '../../constants'
import HybridLink from '../hybrid-link'
import React from 'react'

export default function useRegistrationFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const invalidEmailMessage = formatMessage({
        defaultMessage: 'Please enter a valid email address.',
        id: 'use_registration_fields.error.required_email'
    })
    const labelFirstName = formatMessage({
        defaultMessage: 'First Name',
        id: 'use_registration_fields.label.first_name'
    })

    return {
        firstName: {
            name: `${prefix}firstName`,
            label: labelFirstName,
            type: 'text',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_registration_fields.error.required_first_name'
                }),
                maxLength: {
                    message: formatMessage(generalMessages.maxLength, {
                        label: labelFirstName,
                        n: 40
                    }),
                    value: 40
                }
            },
            error: errors[`${prefix}firstName`],
            inputProps: {
                autoComplete: 'given-name',
                required: 'true'
            },
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_registration_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_registration_fields.error.required_last_name'
                })
            },
            error: errors[`${prefix}lastName`],
            inputProps: {
                autoComplete: 'family-name',
                required: 'true'
            },
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email',
                id: 'use_registration_fields.label.email'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            rules: {
                required: invalidEmailMessage,
                pattern: {
                    value: VALIDATE_EMAIL_PATTERN,
                    message: invalidEmailMessage
                }
            },
            error: errors[`${prefix}email`],
            inputProps: {
                autoComplete: 'email',
                required: 'true'
            },
            control
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'Password',
                id: 'use_registration_fields.label.password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please create a password.',
                    id: 'use_registration_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least 8 characters.',
                            id: 'use_registration_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one uppercase letter.',
                            id: 'use_registration_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one lowercase letter.',
                            id: 'use_registration_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'use_registration_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one special character.',
                            id: 'use_registration_fields.error.special_character'
                        })
                }
            },
            error: errors[`${prefix}password`],
            inputProps: {
                autoComplete: 'new-password',
                required: 'true'
            },
            control
        },
        newsletter: {
            name: `${prefix}newsletter`,
            label: formatMessage(
                {
                    defaultMessage:
                        'Yes, I would like to be informed by email about new products, product features, gift ideas, promotions, personalised offers (e.g. abandoned cart, up- & cross selling) based on my purchase and cart history, collaborations and much more from the Stokke Group (Stokke AS and its subsidiaries). This offer is free of charge, unsubscribe link in every newsletter. I agree to the privacy policy. What personal data we process, why and how: {privacy}.',
                    id: 'forms.marketing2'
                },
                {
                    privacy: (
                        <HybridLink
                            page={HYBRID_CONTENT}
                            href="/customer-service/legal/privacy-policy.html"
                            isExternal
                            fontSize="inherit"
                        >
                            <FormattedMessage
                                defaultMessage="Privacy Policy"
                                id="footer.link.privacy_policy"
                            />
                        </HybridLink>
                    )
                }
            ),
            type: 'checkbox',
            defaultValue: false,
            inputProps: {size: 'sm'},
            control
        }
    }
}
